import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import NavbarV2 from '../components/landing-v2/NavbarV2'
import FooterV2 from '../components/landing-v2/FooterV2'

const SCREENSHOT_DIR = '/landing-v2/blog/teams'

// Section order on the page. Each id maps to `teamsBlog.<id>.*` keys in the
// `pages` namespace and to `${SCREENSHOT_DIR}/<id>.png`.
type SectionId = 'switch' | 'invite' | 'roles' | 'visibility' | 'credits'
const SECTIONS: readonly SectionId[] = ['switch', 'invite', 'roles', 'visibility', 'credits']

export default function BlogTeamsFeaturePage() {
  const { t } = useTranslation('pages')

  return (
    <div data-page="blog-teams-feature" className="min-h-screen bg-black text-white overflow-x-clip">
      <NavbarV2 />

      <main>
        <section className="px-4 pb-[60px] pt-20 sm:px-6 md:px-20 md:pt-[140px]">
          <div className="mx-auto flex max-w-[880px] flex-col gap-4">
            <p className="font-[Fustat] text-sm font-medium uppercase tracking-[0.12em] text-[#a1a1aa]">
              {t('teamsBlog.eyebrow')}
            </p>
            <h1 className="font-[Fustat] text-hero font-medium leading-[1.1] text-white md:text-display-md">
              {t('teamsBlog.title')}
            </h1>
            <p className="font-[Fustat] text-base leading-[1.6] text-[#a1a1aa] md:text-xl md:font-medium">
              {t('teamsBlog.intro')}
            </p>
          </div>
        </section>

        <div className="flex flex-col gap-[80px] px-4 pb-[80px] sm:px-6 md:gap-[120px] md:px-20">
          {SECTIONS.map((id, index) => (
            <section
              key={id}
              data-testid={`teams-blog-section-${id}`}
              className={`mx-auto flex w-full max-w-[1120px] flex-col items-center gap-8 md:gap-14 lg:flex-row ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className="flex flex-col gap-3 lg:w-[380px] lg:shrink-0">
                <span className="font-[Fustat] text-sm font-medium text-[#a1a1aa]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="font-[Fustat] text-2xl font-semibold leading-[1.2] text-white md:text-display-sm">
                  {t(`teamsBlog.${id}.heading`)}
                </h2>
                <p className="font-[Fustat] text-base leading-[1.6] text-[#a1a1aa] md:text-lg">
                  {t(`teamsBlog.${id}.body`)}
                </p>
              </div>
              <figure className="w-full overflow-hidden rounded-[20px] border border-white/[0.08] bg-gradient-to-b from-[#2e3861] via-[#14141f] to-[#0a0a12] p-3 md:rounded-[28px] md:p-5">
                <img
                  src={`${SCREENSHOT_DIR}/${id}.png`}
                  alt={t(`teamsBlog.${id}.alt`)}
                  loading="lazy"
                  className="w-full rounded-xl border border-white/10"
                />
              </figure>
            </section>
          ))}
        </div>

        <section className="px-4 pb-[100px] sm:px-6 md:px-20">
          <div className="mx-auto flex max-w-[880px] flex-col items-start gap-6 rounded-[28px] border border-white/[0.08] bg-[#0d0d0d] p-8 md:p-12">
            <h2 className="font-[Fustat] text-2xl font-semibold leading-[1.2] text-white md:text-display-sm">
              {t('teamsBlog.cta.heading')}
            </h2>
            <p className="font-[Fustat] text-base leading-[1.6] text-[#a1a1aa] md:text-lg">
              {t('teamsBlog.cta.body')}
            </p>
            <Link
              to="/dashboard"
              className="rounded-full bg-white px-6 py-3 font-[Fustat] text-base font-semibold text-black transition-colors hover:bg-white/90"
            >
              {t('teamsBlog.cta.button')}
            </Link>
          </div>
        </section>
      </main>

      <FooterV2 />
    </div>
  )
}
